import { api } from "roboflex-thalamus-request-handler";

/**
 * Check-in / Check-out da visita e QR codes — apenas API (paridade com o app mobile).
 */

function isoPreenchido(val) {
  return val != null && String(val).trim() !== "";
}

function indiceLinhaVisitante(pessoa, loopIdx) {
  const x = pessoa?.index ?? pessoa?.visitante_index;
  if (x != null && x !== "" && !Number.isNaN(Number(x))) return Number(x);
  return loopIdx;
}

/** Linha em `lista_visitantes` pelo índice na solicitação (`visitante_index`). */
function linhaVisitantePorIndex(lista, visitanteIndex) {
  if (!Array.isArray(lista) || lista.length === 0) return null;
  const iv = Number(visitanteIndex);
  if (!Number.isFinite(iv) || iv < 0) return null;
  for (let idx = 0; idx < lista.length; idx++) {
    if (indiceLinhaVisitante(lista[idx], idx) === iv) return lista[idx];
  }
  return lista[iv] ?? null;
}

function statusNumericoLinha(linha) {
  if (!linha || typeof linha !== "object") return null;
  const s = linha.status ?? linha.status_visita ?? linha.status_visitante;
  if (s == null || s === "") return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

/**
 * Check-in concluído só para o visitante do índice (não usa status global da visita em grupo).
 */
function checkinRealizadoParaVisitante({ visitanteIndex, checkinEm, listaVisitantes }) {
  if (isoPreenchido(checkinEm)) return true;
  const linha = linhaVisitantePorIndex(listaVisitantes, visitanteIndex);
  if (linha) {
    if (isoPreenchido(linha.checkin_em ?? linha.checkinEm)) return true;
    const st = statusNumericoLinha(linha);
    if (st != null && st >= 5) return true;
  }
  return false;
}

/**
 * Check-out concluído só para o visitante do índice.
 */
function visitaFinalizadaParaVisitante({
  visitanteIndex,
  checkoutEm,
  listaVisitantes,
  vinculacoesPorVisitante,
  qrcodesUsadosPorVisitante,
  checkinEm,
}) {
  if (isoPreenchido(checkoutEm)) return true;
  const linha = linhaVisitantePorIndex(listaVisitantes, visitanteIndex);
  if (linha) {
    if (isoPreenchido(linha.checkout_em ?? linha.checkoutEm)) return true;
    const st = statusNumericoLinha(linha);
    if (st != null && st >= 6) return true;
  }
  const iv = Number(visitanteIndex);
  const usado = qrcodesUsadosPorVisitante?.[iv];
  const vinculoAtivo = vinculacoesPorVisitante?.[iv];
  if (
    usado &&
    !vinculoAtivo &&
    checkinRealizadoParaVisitante({
      visitanteIndex,
      checkinEm,
      listaVisitantes,
      vinculacoesPorVisitante,
      qrcodesUsadosPorVisitante,
    })
  ) {
    return true;
  }
  return false;
}

function qrVinculadoAtivoParaVisitante(vinculacoesPorVisitante, visitanteIndex) {
  const iv = Number(visitanteIndex);
  return Boolean(vinculacoesPorVisitante?.[iv]);
}

/** Monta `lista_visitantes` a partir da solicitação (fallback quando GET visita falha). */
function montarListaVisitantesSolicitacao(listaRaw) {
  const lista = Array.isArray(listaRaw) ? listaRaw : [];
  return lista.map((v, i) => ({
    index: i,
    nome_completo: String(v.nomeCompleto ?? v.nome_completo ?? "").trim() || `Visitante ${i + 1}`,
  }));
}

function listarQrCodesFixos() {
  return api.get("qrcodes-fixos").then((res) => res.data);
}

/** Vinculações por visitante: [{ visitanteIndex, qrCode }] */
function obterVinculacoesVisita(visitaId) {
  return api.get(`visita/${visitaId}/qrcodes-vinculados`).then((res) => res.data);
}

function obterQrCodeVinculadoVisita(visitaId) {
  return api.get(`visita/${visitaId}/qrcode-vinculado`).then((res) => res.data);
}

function vincularQrCode(visitaId, visitanteIndex, qrCodeId) {
  return api
    .post(`visita/${visitaId}/vincular-qrcode`, { visitante_index: visitanteIndex, qr_code_id: qrCodeId })
    .then((res) => res.data);
}

function desvincularQrCode(visitaId, visitanteIndex = 0) {
  const vi = Number(visitanteIndex);
  const body = Number.isFinite(vi) && vi >= 0 ? { visitante_index: vi } : {};
  return api.post(`visita/${visitaId}/desvincular-qrcode`, body).then((res) => res.data);
}

function obterVisita(id, visitanteIndex = 0) {
  const vi = Number(visitanteIndex);
  const params = Number.isFinite(vi) && vi >= 0 ? { visitante_index: vi } : { visitante_index: 0 };
  return api.get(`visita/${id}`, { params }).then((res) => res.data);
}

function confirmarCheckin(visitaId, payload) {
  return api.post(`visita/${visitaId}/check-in`, payload).then((res) => res.data);
}

function finalizarCheckout(visitaId, payload) {
  return api.post(`visita/${visitaId}/check-out`, payload).then((res) => res.data);
}

export default {
  obterVisita,
  confirmarCheckin,
  finalizarCheckout,
  listarQrCodesFixos,
  obterVinculacoesVisita,
  obterQrCodeVinculadoVisita,
  vincularQrCode,
  desvincularQrCode,
  checkinRealizadoParaVisitante,
  visitaFinalizadaParaVisitante,
  qrVinculadoAtivoParaVisitante,
  montarListaVisitantesSolicitacao,
  linhaVisitantePorIndex,
};

export {
  listarQrCodesFixos,
  obterVinculacoesVisita,
  obterQrCodeVinculadoVisita,
  vincularQrCode,
  desvincularQrCode,
  checkinRealizadoParaVisitante,
  visitaFinalizadaParaVisitante,
  qrVinculadoAtivoParaVisitante,
  montarListaVisitantesSolicitacao,
  linhaVisitantePorIndex,
};
